import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UserContext } from '../UserContext';

const ProtectedRoute = ({ children }) => {
  const { user: contextUser, syncUserWithBackend, lastSyncTime } = useContext(UserContext);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const location = useLocation();

  const SYNC_FRESHNESS_MS = 30 * 1000; // skip sync if done within last 30s

  // Sync user status when accessing routes
  useEffect(() => {
    const syncIfNeeded = async () => {
      const storedUser = localStorage.getItem('sat_user');

      if (!storedUser) {
        setIsCheckingStatus(false);
        return;
      }

      // Skip API call if we synced recently — use localStorage data as-is
      if (lastSyncTime && Date.now() - lastSyncTime < SYNC_FRESHNESS_MS) {
        setIsCheckingStatus(false);
        return;
      }

      if (syncUserWithBackend) {
        try {
          await syncUserWithBackend();
        } catch (error) {
          console.error('Error syncing user:', error);
        }
      }

      setIsCheckingStatus(false);
    };

    syncIfNeeded();
  }, [location.pathname, syncUserWithBackend, lastSyncTime]);

  // Use context user (synced) or fallback to localStorage
  const user = localStorage.getItem('sat_user');
  const userObj = contextUser || (user ? JSON.parse(user) : null);

  // Check if the current route is a public route
  const isAuthRoute = ['/login', '/signup', '/forgot-password'].includes(location.pathname);
  const isPublicRoute = ['/', '/home'].includes(location.pathname);

  // Premium-only routes (exact matches) and prefixes for dynamic routes
  const premiumExact = new Set([
    '/solo',
    '/compete',
    '/choose-topics',
    '/practice',
  ]);
  const premiumPrefixes = [
    '/lobby-multiplayer',
    '/multiplayer/waiting-room/',
    '/multiplayer/game/',
    '/multiplayer/report/',
    '/play-game/',
  ];
  const path = location.pathname;
  const isPremiumPath = premiumExact.has(path) || premiumPrefixes.some(p => path.startsWith(p));

  // IMPORTANT: Do NOT evaluate redirects until checking is complete
  // This prevents premature redirects on page reload
  
  // Show loading while checking status for protected routes
  if (isCheckingStatus) {
    // Only show loading for protected routes, not public/auth routes
    if (!isPublicRoute && !isAuthRoute) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Verifying access...</p>
          </div>
        </div>
      );
    }
    // For public/auth routes, allow them to render while checking
    // They will handle their own redirects if needed
    return children;
  }

  // Only evaluate redirects AFTER checking is complete
  // If user is logged in and tries to access login/signup pages, redirect to options
  if (user && isAuthRoute) {
    return <Navigate to="/options" replace />;
  }

  // If user is not logged in and tries to access protected route, redirect to login
  if (!user && !isPublicRoute && !isAuthRoute) {
    localStorage.setItem('redirect_url', location.pathname);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // If logged in but not premium, block premium-only routes
  if (userObj && userObj.is_premium === false && isPremiumPath) {
    return <Navigate to="/subscription" replace state={{ from: location }} />;
  }

  return children;
};

export default ProtectedRoute;